import create from 'zustand';
import {
  CalendarDatePickerPreset,
  IFilter,
  PerformanceFilterOptionTypeEnum,
} from '@diana/core';
import { useFilterOptions } from './useFilterOptions';
import { DateTime } from 'luxon';
import { DataRangePresets, IExtendedFiltersValues } from '../utils/convertFiltersToQuery';
import { updateBrowserQueryParams } from '../utils/urlUtils';
import { getDefaultTimeRange } from '../utils/timeRangeUtils';

export type { IExtendedFiltersValues };

export interface IFiltersStore {
  availableFilters: IFilter[];
  filtersValues: IExtendedFiltersValues;
  filters: string[];
  setAvailableFilters: (payload: IFilter[]) => void;
  setFilters: (payload: string[]) => void;
  setFiltersValues: (payload: IExtendedFiltersValues) => void;
  resetData: () => void;
  defaultFiltersValues: IExtendedFiltersValues;
  setDefaultFiltersValues: (payload: IExtendedFiltersValues) => void;
  isTimeRangeEndsBeforeNow: () => boolean;
  getTimeRangePreset: () => CalendarDatePickerPreset | null;
}

export enum FilterType {
  BARGES = 'barges',
  MFM_DEVICES = 'mfmDevice',
  BMT_NUMBER = 'BMT',
  TIME_RANGE = 'timeRange',
}

const defaultFilters = [
  {
    property: {
      key: FilterType.BARGES,
      displayName: 'Barges',
      description: 'Barges',
      defaultOn: true,
      isDefaultProperty: true,
      dataPath: null,
      valuePath: '$.displayName',
      valuePaths: ['$.displayName'],
      filterType: 0,
      colPosition: 0,
    },
    renderFilter: () => null,
  },
  {
    property: {
      key: FilterType.MFM_DEVICES,
      displayName: 'MFM Device',
      description: 'MFM Device',
      defaultOn: true,
      isDefaultProperty: true,
      dataPath: null,
      valuePath: '$.displayName',
      valuePaths: ['$.displayName'],
      filterType: 0,
      colPosition: 1,
    },
    renderFilter: () => null,
  },
  {
    property: {
      key: FilterType.BMT_NUMBER,
      displayName: 'BMT Number',
      description: 'BMT Number',
      defaultOn: true,
      isDefaultProperty: true,
      dataPath: null,
      valuePath: '$.displayName',
      valuePaths: ['$.displayName'],
      filterType: 0,
      colPosition: 2,
      type: PerformanceFilterOptionTypeEnum.ENUM,
    },
    renderFilter: () => null,
  },
  {
    property: {
      key: FilterType.TIME_RANGE,
      displayName: 'Time Range',
      description: 'Time Range',
      defaultOn: true,
      isDefaultProperty: true,
      dataPath: null,
      valuePath: '$.displayName',
      valuePaths: ['$.displayName'],
      filterType: 0,
      colPosition: 3,
      type: PerformanceFilterOptionTypeEnum.DATE_TIME_RANGE,
    },
    renderFilter: () => null,
  },
];

const createInitialState = (customFiltersValues?: Partial<IExtendedFiltersValues>) => ({
  defaultFiltersValues: {},
  filtersValues: {
    barges: [],
    mfmDevice: [],
    bmtNumber: [],
    timeRange: getDefaultTimeRange(),
    ...customFiltersValues,
  },
  availableFilters: defaultFilters,
  filters: [
    FilterType.BARGES,
    FilterType.MFM_DEVICES,
    FilterType.BMT_NUMBER,
    FilterType.TIME_RANGE,
  ],
});

const createFiltersStore = (storeId: string, customFiltersValues?: Partial<IExtendedFiltersValues>) => {
  const initialState = createInitialState(customFiltersValues);
  
  return create<IFiltersStore>((set, get) => ({
    ...initialState,
    setAvailableFilters: (payload) => {
      set({ availableFilters: payload });
    },
    setFilters: (payload) => set({ filters: payload }),
    setFiltersValues: (payload: IExtendedFiltersValues) => {
      const currentFiltersValues = get().filtersValues;
      const hasChanged = JSON.stringify(currentFiltersValues) !== JSON.stringify(payload);
      if (!hasChanged) {
        return;
      }
      
      const options = useFilterOptions.getState().data;
      if (payload[FilterType.BMT_NUMBER] && options?.[FilterType.BMT_NUMBER]) {
        payload[FilterType.BMT_NUMBER] = payload[FilterType.BMT_NUMBER].map((bmt) => {
          if (options[FilterType.BMT_NUMBER]) {
            return options[FilterType.BMT_NUMBER][0].options.find(
              (option: any) => option.value === bmt.value,
            );
          } else return undefined;
        });
      }
      updateBrowserQueryParams(payload);
      set({ filtersValues: payload });
    },
    setDefaultFiltersValues: (payload) => set({ defaultFiltersValues: payload }),
    resetData: () => {
      set(initialState);
    },
    isTimeRangeEndsBeforeNow: () => {
      const filtersValues = get().filtersValues;
      const timeRange = filtersValues[FilterType.TIME_RANGE];

      if (timeRange && timeRange.length > 0 && timeRange[0]?.value && timeRange[1]?.value) {
        if (
          timeRange[0].preset &&
          [...DataRangePresets, CalendarDatePickerPreset.CurrentSession, CalendarDatePickerPreset.LastWeekTime, CalendarDatePickerPreset.LastTwoDays].includes(
            timeRange[0].preset,
          )
        ) {
          return false;
        }
        const startTime = DateTime.fromISO(timeRange[0].value as string);
        const endTime = DateTime.fromISO(timeRange[1].value as string);
        const now = DateTime.now();

        return startTime < now && endTime < now;
      }
      return false;
    },
    getTimeRangePreset: () => {
      const filtersValues = get().filtersValues;
      const timeRange = filtersValues[FilterType.TIME_RANGE];
      if (timeRange && timeRange.length > 0 && timeRange[0].preset) {
        return timeRange[0].preset;
      } else {
        return null;
      }
    },
  }));
};

const storeInstances = new Map<string, ReturnType<typeof createFiltersStore>>();

export const useFiltersStore = (storeId: string, customFiltersValues?: Partial<IExtendedFiltersValues>) => {
  if (!storeInstances.has(storeId)) {
    storeInstances.set(storeId, createFiltersStore(storeId, customFiltersValues));
  }
  return storeInstances.get(storeId)!();
};

export const clearFiltersStore = (storeId: string) => {
  storeInstances.delete(storeId);
};

export const resetFiltersStore = (storeId: string, customFiltersValues?: Partial<IExtendedFiltersValues>) => {
  if (storeInstances.has(storeId)) {
    const store = storeInstances.get(storeId)!;
    store.getState().resetData();
  }
};
